<template>
  <div class="logs-card logs-filtros-tab">
    <div class="row">
      <div class="col-lg-4 col-md-4 col-sm-6">
        <div class="form-group">
          <label>Equipe</label>

          <vue-select
            :options="equipes"
            :value="equipeSelecionada"
            :get-option-label="descricaoEquipe"
            :disabled="loadingEquipes || possuiProfissionalSelecionado"
            :loading="loadingEquipes"
            placeholder="Todas"
            @input="handleEquipe"
          >
            <template slot="option" slot-scope="option">
              {{ descricaoEquipe(option) }}
            </template>

            <div slot="no-options">Não há opções correspondentes</div>
          </vue-select>
        </div>
      </div>

      <div class="col-lg-4 col-md-4 col-sm-6">
        <div class="form-group">
          <label>Profissional</label>

          <vue-select
            :options="profissionais"
            :value="profissionalSelecionado"
            :get-option-label="descricaoProfissional"
            :disabled="loadingProfissionais || possuiEquipeSelecionada"
            :loading="loadingProfissionais"
            placeholder="Todos"
            @input="handleProfissional"
          >
            <template slot="option" slot-scope="option">
              {{ descricaoProfissional(option) }}
            </template>

            <div slot="no-options">Não há opções correspondentes</div>
          </vue-select>
        </div>
      </div>

      <div class="col-lg-4 col-md-4 col-sm-6">
        <div class="form-group">
          <label>Funcionalidade</label>

          <vue-select
            :options="funcionalidades"
            :value="funcionalidadeSelecionada"
            :get-option-label="descricaoFuncionalidade"
            :disabled="loadingFuncionalidades"
            :loading="loadingFuncionalidades"
            placeholder="Todas"
            @input="handleFuncionalidade"
          >
            <template slot="option" slot-scope="option">
              {{ descricaoFuncionalidade(option) }}
            </template>

            <div slot="no-options">Não há opções correspondentes</div>
          </vue-select>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FiltrosTabLogs',

  props: {
    equipes: {
      type: Array,
      default: () => [],
    },

    profissionais: {
      type: Array,
      default: () => [],
    },

    funcionalidades: {
      type: Array,
      default: () => [],
    },

    loadingEquipes: {
      type: Boolean,
      default: false,
    },

    loadingProfissionais: {
      type: Boolean,
      default: false,
    },

    loadingFuncionalidades: {
      type: Boolean,
      default: false,
    },

    filtros: {
      type: Object,
      required: true,
    },
  },

  computed: {
    /**
     * Retorna a equipe atualmente selecionada.
     *
     * @returns {Object|null}
     */
    equipeSelecionada() {
      return (
        this.equipes.find((equipe) => equipe.ine === this.filtros.ine) || null
      )
    },

    /**
     * Retorna o profissional atualmente selecionado.
     *
     * @returns {Object|null}
     */
    profissionalSelecionado() {
      return (
        this.profissionais.find(
          (profissional) => profissional.id_user === this.filtros.profissional
        ) || null
      )
    },

    /**
     * Retorna a funcionalidade atualmente selecionada.
     *
     * @returns {Object|null}
     */
    funcionalidadeSelecionada() {
      return (
        this.funcionalidades.find(
          (funcionalidade) => funcionalidade.id === this.filtros.funcionalidade
        ) || null
      )
    },

    /**
     * Indica se existe uma equipe selecionada.
     *
     * @returns {Boolean}
     */
    possuiEquipeSelecionada() {
      return !!this.filtros.ine
    },

    /**
     * Indica se existe um profissional selecionado.
     *
     * @returns {Boolean}
     */
    possuiProfissionalSelecionado() {
      return !!this.filtros.profissional
    },
  },

  methods: {
    /**
     * Trata a seleção da equipe.
     *
     * @param {Object|null} equipe Equipe selecionada
     * @returns {void}
     */
    handleEquipe(equipe) {
      const filtros = {
        ...this.filtros,
        ine: equipe ? equipe.ine : '',
        profissional: '',
      }

      this.emitirAlteracao(filtros, 'ine')
    },

    /**
     * Trata a seleção do profissional.
     *
     * @param {Object|null} profissional Profissional selecionado
     * @returns {void}
     */
    handleProfissional(profissional) {
      const filtros = {
        ...this.filtros,
        profissional: profissional ? profissional.id_user : '',
        ine: '',
      }

      this.emitirAlteracao(filtros, 'profissional')
    },

    /**
     * Trata a seleção da funcionalidade.
     *
     * @param {Object|null} funcionalidade Funcionalidade selecionada
     * @returns {void}
     */
    handleFuncionalidade(funcionalidade) {
      const filtros = {
        ...this.filtros,
        funcionalidade: funcionalidade ? funcionalidade.id : '',
      }

      this.emitirAlteracao(filtros, 'funcionalidade')
    },

    /**
     * Emite os filtros atualizados para o componente pai.
     *
     * @param {Object} filtros Filtros atualizados
     * @param {String} campo Campo que originou a alteração
     * @returns {void}
     */
    emitirAlteracao(filtros, campo) {
      this.$emit('alterar', filtros, campo)
    },

    /**
     * Retorna a descrição da equipe para exibição no select.
     *
     * @param {Object} equipe Dados da equipe
     * @returns {String}
     */
    descricaoEquipe(equipe) {
      if (equipe.no_ubs) {
        return `${equipe.ine} - ${equipe.no_ubs}`
      }

      return equipe.ine || ''
    },

    /**
     * Retorna o nome do profissional para exibição no select.
     *
     * @param {Object} profissional Dados do profissional
     * @returns {String}
     */
    descricaoProfissional(profissional) {
      return profissional.name_user || ''
    },

    /**
     * Retorna o nome da funcionalidade para exibição no select.
     *
     * @param {Object} funcionalidade Dados da funcionalidade
     * @returns {String}
     */
    descricaoFuncionalidade(funcionalidade) {
      return funcionalidade.card || ''
    },
  },
}
</script>
